<!-- @format -->

<template>
	<div class="authorized-person-modal">
		<AuthorizedPersonFormFields
			v-if="!showOnlyPassportUpload"
			ref="fields"
			class="mb-3"
			:modelValue="authorizedPersonStore.inputs"
			:validator="v$"
			:disabled="disableInputs"
			@update:modelValue="handleFieldsUpdate"
		/>

		<CoreTwoColumnsRow class="mb-3">
			<template #left>
				<CoreBorderWrapper
					class="flex-grow-1 flex-shrink-1 flex-basis-40"
					:condensed="true"
				>
					{{ $t('§eq_operator.auth_person_passport') }}
				</CoreBorderWrapper>
			</template>

			<template #right>
				<CoreFileUpload
					class="flex-grow-1 flex-shrink-1 flex-basis-60"
					name="passport"
					v-tooltip="
						!authorizedPersonStore.inputs.id
							? {
									value: $t('§ui.tooltip_save_auth_person_before_upload'),
								}
							: undefined
					"
					:auto="true"
					:disabled="
						authorizedPersonStore.inputs.id === null ||
						authorizedPersonStore.inputs.archived
					"
					:filePath="authorizedPersonStore.inputs.passport_file_path"
					:uploadUrl="uploadUrl"
					@download="handleDownload"
					@delete="handleDelete"
					@uploaded="handleUploadFinished"
				/>
			</template>
		</CoreTwoColumnsRow>

		<CoreTwoColumnsRow class="mb-3">
			<template #left>
				<CoreBorderWrapper
					class="flex-grow-1 flex-shrink-1 flex-basis-40"
					:condensed="true"
				>
					{{ $t('§eq_operator.auth_person_id_verified') }}
				</CoreBorderWrapper>
			</template>

			<template #right>
				<div class="flex-grow-1 flex-shrink-1 flex-basis-60 flex align-items-center gap-2">
					<CoreCheckbox
						name="id_verified"
						:disabled="disableInputs"
						:modelValue="authorizedPersonStore.inputs.id_verified"
						@update:modelValue="authorizedPersonStore.inputs.id_verified = $event"
					/>
					<span v-if="authorizedPersonStore.inputs.id_verification_date">
						{{ displayAsDate(authorizedPersonStore.inputs.id_verification_date) }}
					</span>
				</div>
			</template>
		</CoreTwoColumnsRow>

		<div class="flex justify-start gap-2">
			<CoreButton
				v-if="!showOnlyPassportUpload"
				label="§ui_button.save"
				icon="pi-save"
				@click="handleSave"
				:loading="saving"
				:disabled="!isValid || disableInputs"
			/>

			<div
				v-if="authorizedPersonStore.inputs.id && !showOnlyPassportUpload"
				v-tooltip="
					hasUnsavedChanges
						? {
								value: $t('§ui.tooltip_save_auth_person_before_archiving'),
							}
						: undefined
				"
			>
				<CoreButton
					variant="secondary"
					:label="
						authorizedPersonStore.inputs.archived
							? '§ui_button.unarchive'
							: '§ui_button.archive'
					"
					:icon="authorizedPersonStore.inputs.archived ? 'pi-replay' : 'pi-inbox'"
					:disabled="disableArchiveToggle"
					@click="handleArchiveToggle"
				/>
			</div>

			<CoreButton
				variant="tertiary"
				label="§ui_button.cancel"
				@click="modalStore.setClose"
				icon="pi-times"
			/>
		</div>
	</div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength } from '@vuelidate/validators'
import { useAuthorizedPersonStore } from '@/stores/authorizedPerson'
import { useModalStore } from '@/stores/modal'
import { useClientStore } from '@/stores/client'
import AuthorizedPersonFormFields from './components/AuthorizedPersonFormFields.vue'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreFileUpload from '@/components/core/CoreFileUpload.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import { signatoryResidencyCountry } from '@/helpers/signatoryResidencyCountry'

const rules = {
	first_name: {
		required,
		minLength: minLength(2),
		maxLength: maxLength(200),
	},
	last_name: {
		required,
		minLength: minLength(2),
		maxLength: maxLength(200),
	},
	date_of_birth: {
		required,
	},
	country_id: {
		required,
		signatoryResidencyCountry,
	},
	address_country_id: {
		signatoryResidencyCountry,
	},
}

export default {
	name: 'AuthorizedPersonModal',

	components: {
		AuthorizedPersonFormFields,
		CoreBorderWrapper,
		CoreTwoColumnsRow,
		CoreFileUpload,
		CoreButton,
		CoreCheckbox,
	},

	data() {
		return {
			saving: false,
			closeModalAfterUploading: false,

			file: {
				fileId: null,
				uppyFileName: '',
				error: '',
				isFileSelected: false,
			},

			v$: {},
		}
	},

	computed: {
		uploadUrl() {
			if (this.authorizedPersonStore.inputs.id) {
				return this.route('authorized_person_passport.upload', [
					this.authorizedPersonStore.inputs.id,
				])
			}
			return ''
		},

		isValid() {
			return !this.v$.$invalid
		},

		showOnlyPassportUpload() {
			return this.modalStore.data.showOnlyPassportUpload
		},

		disableInputs() {
			return (
				this.modalStore.data.disableInputs ||
				this.authorizedPersonStore.saving ||
				this.authorizedPersonStore.inputs.archived
			)
		},

		hasUnsavedChanges() {
			return Boolean(this.authorizedPersonStore.isDirty)
		},

		disableArchiveToggle() {
			return (
				this.modalStore.data.disableInputs ||
				this.authorizedPersonStore.saving ||
				this.hasUnsavedChanges
			)
		},
	},

	created() {
		this.authorizedPersonStore = useAuthorizedPersonStore()
		this.modalStore = useModalStore()
		this.clientStore = useClientStore()
	},

	mounted() {
		this.$nextTick(() => {
			this.v$ = useVuelidate(rules, this.authorizedPersonStore.inputs)

			if (this.authorizedPersonStore.inputs.id) {
				// A saved country may have been unticked since; show the error right away, not only a disabled Save.
				;['country_id', 'address_country_id'].forEach(field => {
					if (this.v$[field].$invalid) {
						this.v$[field].$touch()
					}
				})
			}

			if (this.$refs.fields && typeof this.$refs.fields.focusFirstName === 'function') {
				this.$refs.fields.focusFirstName()
			}
		})
	},

	beforeUnmount() {
		this.authorizedPersonStore.clearPerson()
	},

	methods: {
		handleFieldsUpdate(patch) {
			Object.assign(this.authorizedPersonStore.inputs, patch)
		},

		async handleSave() {
			if (this.authorizedPersonStore.saving) {
				return
			}
			this.file.isFileSelected = false
			if (this.authorizedPersonStore.inputs.id) {
				await this.authorizedPersonStore.update()
			} else {
				await this.authorizedPersonStore.create()
			}
		},

		handleUploadFinished(xhr) {
			const { passport_file_path } = JSON.parse(xhr.response)
			this.authorizedPersonStore.addPassportFilePath(passport_file_path)
		},

		handleDownload() {
			this.authorizedPersonStore.downloadPassport({
				id: this.authorizedPersonStore.inputs.id,
				first_name: this.authorizedPersonStore.inputs.first_name,
				last_name: this.authorizedPersonStore.inputs.last_name,
			})
		},

		handleDelete() {
			this.authorizedPersonStore.deletePassport()
		},

		handleArchiveToggle() {
			const authorizedPersonStore = this.authorizedPersonStore
			const modalStore = this.modalStore
			const person = this.clientStore.clientData.authorized_people.find(
				(p) => p.id === authorizedPersonStore.inputs.id,
			)
			if (!person) {
				return
			}

			modalStore.setOpen({
				name: 'ConfirmActionModal',
				size: 'small',
				title: person.archived
					? '§eq_operator.auth_person_confirm_unarchive_modal'
					: '§eq_operator.auth_person_confirm_archive_modal',
				data: {
					type: 'confirm',
					buttonLabel: this.$t(
						person.archived ? '§ui_button.unarchive' : '§ui_button.archive',
					),
					onConfirm: async () => {
						await authorizedPersonStore.toggleArchived({ authorizedPerson: person })
						modalStore.setOpen({
							name: 'AuthorizedPersonModal',
							title: '§eq_operator.auth_person_modal_title',
							size: 'large',
							data: {
								showOnlyPassportUpload: false,
							},
						})
					},
				},
			})
		},
	},
}
</script>
